import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEye } from 'react-icons/fi';
import {
  AdminPopup,
  DateRegistrationStats,
  EmailComposer,
  ExportData,
  GroupEmail,
  Header,
  TimeSlotStats,
} from '../../../components/admin';
import GradientStrip from '../../../components/gradientStrip';
import {
  schoolFormEstablishmentTypes,
  otherGroupsFormEstablishmentTypes,
} from '../../../components/registrationFormComponents/data';
import { AdminContext } from '../../../contexts/admin.context';
import {
  adminApi,
  exportData,
  getRegistrationStats,
  getDateRegistrationStats,
  getHourlyTimeSlotStats,
  getSlotReservationStats,
  sendEmailMessage,
} from '../../../service';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { adminData } = useContext(AdminContext);

  // Function to get initial active tab based on URL hash
  const getInitialActiveTab = () => {
    const hash = location.hash.replace('#', '');
    if (hash === 'school') return 'scolaire';
    if (hash === 'other-group') return 'autre';
    return 'apercu';
  };

  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateStatsData, setDateStatsData] = useState(null);
  const [isDateStatsLoading, setIsDateStatsLoading] = useState(true);
  const [timeSlotData, setTimeSlotData] = useState(null);
  const [slotReservationData, setSlotReservationData] = useState(null);
  const [isTimeSlotDataLoading, setIsTimeSlotDataLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(getInitialActiveTab);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
  const [emailSelections, setEmailSelections] = useState(null);
  const [emailComposerKey, setEmailComposerKey] = useState(0);
  const [selectedExportOptions, setSelectedExportOptions] = useState({
    allData: false,
    schoolTypes: [],
    otherGroupTypes: [],
    experienceTypes: [],
  });
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const exportAbortControllerRef = useRef(null);
  const emailAbortControllerRef = useRef(null);

  // Dashboard data
  const allTabs = [
    { id: 'apercu', label: 'APERÇU', type: 'all' },
    { id: 'scolaire', label: 'SCOLAIRE', type: 'school' },
    { id: 'autre', label: 'AUTRE GROUPE', type: 'other-group' },
  ];

  // Filter tabs based on assigned registration types
  const tabs = allTabs.filter(
    (tab) =>
      // Only show apercu tab if admin has more than one registration type
      (tab.id === 'apercu' &&
        adminData?.assignedRegistrationTypes &&
        adminData.assignedRegistrationTypes.length > 1) ||
      (adminData?.assignedRegistrationTypes &&
        adminData.assignedRegistrationTypes.includes(tab.type)),
  );

  // Update active tab based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const isValidTab = tabs.some((tab) => tab.type === hash);
      if (isValidTab) {
        const tabId =
          hash === 'all'
            ? 'apercu'
            : hash === 'school'
              ? 'scolaire'
              : hash === 'other-group'
                ? 'autre'
                : hash;

        setActiveTab(tabId);
      } else if (tabs.length > 0) {
        navigate({ hash: tabs[0].type }, { replace: true });
      }
    }
  }, [location.hash, tabs, navigate]);

  // Set default active tab if apercu is hidden
  useEffect(() => {
    if (tabs.length > 0 && activeTab === 'apercu' && !tabs.some((tab) => tab.id === 'apercu')) {
      // Find the first available tab that isn't 'apercu'
      const firstAvailableTab = tabs[0];

      // Only update if it's actually different
      if (firstAvailableTab.id !== activeTab) {
        setActiveTab(firstAvailableTab.id);
        navigate({ hash: firstAvailableTab.type });
      }
    }
  }, [tabs, activeTab, navigate]);

  // Handle tab click
  const handleTabClick = (tabId, tabType) => {
    // Only navigate - let the URL hash useEffect handle setting activeTab
    navigate({ hash: tabType });
  };

  // Generate columns based on API data
  const getColumns = () => {
    if (!statsData) return [];

    // Helper function to format item name
    const formatName = (name, type) => {
      if (name === 'Total') return 'Tous les groupes';
      if (name === 'other-group') return 'Autre Groupes';
      if (name === 'school') return 'Scolaire';

      // Check in establishment type mappings
      return (
        schoolFormEstablishmentTypes.find((t) => t.value === name)?.label ||
        otherGroupsFormEstablishmentTypes.find((t) => t.value === name)?.label ||
        name
      );
    };

    // Helper function to map stat items consistently
    const mapItems = (items) => {
      return items.map((item) => ({
        name: formatName(item.name),
        establishments: item.establishments,
        people: item.people,
        type: item.name === 'Total' ? 'all' : item.name,
      }));
    };

    // Define all possible columns
    const allColumns = [
      {
        id: 'total',
        title: 'TOTAL',
        type: 'all',
        items: statsData.overall ? mapItems(statsData.overall) : [],
      },
      {
        id: 'scolaire',
        title: 'SCOLAIRE',
        type: 'school',
        items: statsData.school ? mapItems(statsData.school) : [],
      },
      {
        id: 'autre',
        title: 'AUTRE GROUPES',
        type: 'other-group',
        items: statsData['other-group'] ? mapItems(statsData['other-group']) : [],
      },
    ];

    // Filter empty columns
    const nonEmptyColumns = allColumns.filter(
      (column) => Array.isArray(column.items) && column.items.length > 0,
    );

    // Filter columns based on assigned registration types
    const permissionFilteredColumns = nonEmptyColumns.filter((column) => {
      // Keep total column only if admin has more than one registration type
      if (column.id === 'total') {
        return (
          adminData?.assignedRegistrationTypes && adminData.assignedRegistrationTypes.length > 1
        );
      }

      // Otherwise, check if admin has permission for this column type
      return (
        adminData?.assignedRegistrationTypes &&
        adminData.assignedRegistrationTypes.includes(column.type)
      );
    });

    // Filter columns based on active tab
    if (activeTab !== 'apercu') {
      return permissionFilteredColumns.filter((column) => column.id === activeTab);
    }

    return permissionFilteredColumns;
  };

  const handleLogout = () => {
    try {
      // Remove the auth token from localStorage
      localStorage.removeItem('adminToken');

      // Remove the Authorization header
      delete adminApi.defaults.headers.common['Authorization'];

      // Show success message
      toast.success(t('logout.success') || 'Déconnexion réussie');

      // Navigate to login page
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('logout.error') || 'Erreur lors de la déconnexion');
    }
  };

  // Component to render a single statistics card
  const StatCard = ({ item, columnId }) => {
    const handleCardClick = () => {
      const queryParams = new URLSearchParams();

      // Special handling for total column with school or other-group items
      if (columnId === 'total' && ['school', 'other-group'].includes(item.type)) {
        queryParams.append('registrationType', item.type);
        queryParams.append('type', 'all');
      } else {
        // Simple mapping of column IDs to registration types
        let registrationType = 'all';
        if (columnId === 'scolaire') registrationType = 'school';
        if (columnId === 'autre') registrationType = 'other-group';

        queryParams.append('registrationType', registrationType);
        queryParams.append('type', item.type);
      }

      navigate(`/admin/dashboard/registrations?${queryParams.toString()}`);
    };

    // Check if this is the 'Tous les groupes' item
    const isTousLesGroupes = item.name === 'Tous les groupes';

    return (
      <div
        className={`p-4 m-2 rounded-lg cursor-pointer transition-colors shadow-sm text-gray-50 ${
          isTousLesGroupes ? 'hover:opacity-90 bg-gray-60' : 'bg-teal-10 hover:bg-teal-30'
        }`}
        onClick={handleCardClick}>
        <div className="font-medium mb-2 text-gray-50">{item.name}</div>
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-gray-50">Total établissements</span>
          <span className={`font-medium text-right ${isTousLesGroupes ? 'text-gray-50' : ''}`}>
            {item.establishments}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-50">Total personnes</span>
          <span className={`font-medium text-right ${isTousLesGroupes ? 'text-gray-50' : ''}`}>
            {item.people}
          </span>
        </div>
      </div>
    );
  };

  // Skeleton loading card component
  const SkeletonCard = () => {
    return (
      <div className="bg-teal-10/40 p-4 m-2 rounded-lg shadow-sm">
        <div className="mb-2">
          <div className="h-5 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-3/4"></div>
        </div>
        <div className="flex justify-between text-xs mb-1.5">
          <div className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-1/2"></div>
          <div className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-1/6"></div>
        </div>
        <div className="flex justify-between text-xs">
          <div className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-1/2"></div>
          <div className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-1/6"></div>
        </div>
      </div>
    );
  };

  // Skeleton tab component
  const SkeletonTab = ({ width }) => {
    return (
      <div className={`py-2 px-6 rounded-lg bg-teal-10/40 min-w-0 ${width}`}>
        <div className="h-4 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse"></div>
      </div>
    );
  };

  // Skeleton tab bar
  const SkeletonTabBar = () => {
    return (
      <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap mb-8 md:mb-16">
        <div className="py-2 px-6 rounded-lg bg-teal-10/40">
          <div className="h-4 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-20"></div>
        </div>

        {adminData?.assignedRegistrationTypes?.includes('school') && (
          <SkeletonTab width="md:w-24" />
        )}

        {adminData?.assignedRegistrationTypes?.includes('other-group') && (
          <SkeletonTab width="md:w-28" />
        )}

        <div className="hidden md:block md:flex-grow"></div>

        <div className="py-2 px-6 rounded-lg bg-teal-10/40 flex items-center space-x-2 col-span-2 justify-self-end">
          <div className="h-5 w-5 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse"></div>
          <div className="h-4 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-16"></div>
        </div>
      </div>
    );
  };

  const getRegistrationData = async () => {
    setIsLoading(true);
    try {
      const response = await getRegistrationStats();
      setStatsData(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors de la récupération des statistiques');
    } finally {
      setIsLoading(false);
    }
  };

  const getDateStats = async () => {
    setIsDateStatsLoading(true);
    try {
      // Determine registration type based on active tab
      let registrationType = 'all';
      if (activeTab === 'scolaire') {
        registrationType = 'school';
      } else if (activeTab === 'autre') {
        registrationType = 'other-group';
      }

      const response = await getDateRegistrationStats(registrationType);
      setDateStatsData(response.data);
    } catch (error) {
      console.error('Error fetching date stats:', error);
      toast.error('Erreur lors de la récupération des statistiques de date');
    } finally {
      setIsDateStatsLoading(false);
    }
  };

  const getTimeSlotStatsData = async () => {
    setIsTimeSlotDataLoading(true);
    try {
      // Determine registration type based on active tab
      let registrationType = 'all';
      if (activeTab === 'scolaire') {
        registrationType = 'school';
      } else if (activeTab === 'autre') {
        registrationType = 'other-group';
      }

      const [hourlyResponse, reservationResponse] = await Promise.all([
        getHourlyTimeSlotStats(registrationType),
        getSlotReservationStats('intro'),
      ]);
      setTimeSlotData(hourlyResponse.data);
      setSlotReservationData(reservationResponse.data);
    } catch (error) {
      console.error('Error fetching time slot stats:', error);
      toast.error('Erreur lors de la récupération des statistiques par créneaux horaires');
    } finally {
      setIsTimeSlotDataLoading(false);
    }
  };

  const handleRefreshClick = () => {
    getRegistrationData();
    getDateStats();
    getTimeSlotStatsData();
  };

  // Reload registration statistics when the selected tab changes.
  useEffect(() => {
    if (!activeTab) return;

    setDateStatsData(null);
    setTimeSlotData(null);
    setSlotReservationData(null);
    setIsDateStatsLoading(true);
    setIsTimeSlotDataLoading(true);

    getRegistrationData();
    getDateStats();
    getTimeSlotStatsData();
  }, [activeTab]);

  // Get columns based on the statistics data
  const columns = !isLoading && statsData ? getColumns() : [];

  // Get skeleton columns to display based on active tab
  const getSkeletonColumns = () => {
    if (activeTab === 'apercu') {
      return (
        <>
          <div className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
            <div className="h-6 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-16 mb-4"></div>
            <div className="flex flex-col space-y-6 border rounded p-2">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>

          {adminData?.assignedRegistrationTypes?.includes('school') && (
            <div className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
              <div className="h-6 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-20 mb-4"></div>
              <div className="flex flex-col space-y-6 border rounded p-2">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            </div>
          )}

          {adminData?.assignedRegistrationTypes?.includes('other-group') && (
            <div className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
              <div className="h-6 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-28 mb-4"></div>
              <div className="flex flex-col space-y-6 border rounded p-2">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            </div>
          )}
        </>
      );
    } else if (
      activeTab === 'scolaire' &&
      adminData?.assignedRegistrationTypes?.includes('school')
    ) {
      return (
        <div className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
          <div className="h-6 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-20 mb-4"></div>
          <div className="flex flex-col space-y-6 border rounded p-2">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      );
    } else if (
      activeTab === 'autre' &&
      adminData?.assignedRegistrationTypes?.includes('other-group')
    ) {
      return (
        <div className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
          <div className="h-6 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-28 mb-4"></div>
          <div className="flex flex-col space-y-6 border rounded p-2">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      );
    }

    return null;
  };

  const handleOpenEmailPopup = () => {
    setIsEmailPopupOpen(true);
    setIsEmailComposerOpen(false);
  };

  const handleCloseEmailPopup = () => {
    // Abort any ongoing email request
    if (emailAbortControllerRef.current) {
      emailAbortControllerRef.current.abort();
      emailAbortControllerRef.current = null;
      setIsEmailSending(false);
    }
    setIsEmailPopupOpen(false);
    setIsEmailComposerOpen(false);
    // Reset email selections when popup is closed
    setEmailSelections(null);
    // Update the key to force EmailComposer to re-render with empty fields
    setEmailComposerKey((prevKey) => prevKey + 1);
  };

  const handleEmailSelections = (selections) => {
    setEmailSelections(selections);
  };

  const hasSelections = () => {
    if (!emailSelections) return false;

    // Check if email consent is selected (required)
    const hasEmailConsent =
      emailSelections.emailConsent !== null && emailSelections.emailConsent !== undefined;

    // Check if at least one other filter is selected
    const hasOtherFilters =
      (emailSelections.establishmentTypes && emailSelections.establishmentTypes.length > 0) ||
      (emailSelections.otherGroups && emailSelections.otherGroups.length > 0) ||
      (emailSelections.dates && emailSelections.dates.length > 0) ||
      (emailSelections.picnic && (emailSelections.picnic.oui || emailSelections.picnic.non)) ||
      (emailSelections.handicap &&
        (emailSelections.handicap.oui || emailSelections.handicap.non)) ||
      (emailSelections.transportModes && emailSelections.transportModes.length > 0);

    // Both email consent AND at least one filter must be selected
    return hasEmailConsent && hasOtherFilters;
  };

  const handleWriteEmail = () => {
    setIsEmailComposerOpen(true);
  };

  const handleSendEmail = async (emailData) => {
    if (!emailSelections) {
      toast.error('No recipients selected');
      return;
    }

    try {
      setIsEmailSending(true);

      // Cancel any ongoing email request
      if (emailAbortControllerRef.current) {
        emailAbortControllerRef.current.abort();
      }

      // Create a new AbortController
      const controller = new AbortController();
      emailAbortControllerRef.current = controller;

      // Only include selected items from emailSelections
      const filteredSelections = {
        // Only include establishment types that have selections
        establishmentTypes:
          emailSelections.establishmentTypes.length > 0
            ? emailSelections.establishmentTypes
            : undefined,

        // Only include other groups that have selections
        otherGroups:
          emailSelections.otherGroups.length > 0 ? emailSelections.otherGroups : undefined,

        // Only include dates that have selections
        dates: emailSelections.dates.length > 0 ? emailSelections.dates : undefined,

        // Convert picnic object to array of selected options
        picnic:
          emailSelections.picnic.oui || emailSelections.picnic.non
            ? Object.entries(emailSelections.picnic)
                .filter(([_, isSelected]) => isSelected)
                .map(([option]) => option)
            : undefined,

        // Convert handicap object to array of selected options
        handicap:
          emailSelections.handicap.oui || emailSelections.handicap.non
            ? Object.entries(emailSelections.handicap)
                .filter(([_, isSelected]) => isSelected)
                .map(([option]) => option)
            : undefined,

        // Only include transport modes that have selections
        transportModes:
          emailSelections.transportModes.length > 0 ? emailSelections.transportModes : undefined,

        // Email consent - If nothing selected → undefined, 'oui' → true, 'non' → false
        emailConsent:
          emailSelections.emailConsent === null || emailSelections.emailConsent === undefined
            ? undefined
            : emailSelections.emailConsent === 'oui'
              ? true
              : false,
      };

      // Make the email request
      const response = await sendEmailMessage(
        {
          type: 'group',
          selections: filteredSelections,
          emailContent: {
            subject: emailData.subject,
            message: emailData.body,
          },
          recipientType: emailData.recipientType,
        },
        controller.signal,
      );

      // Handle JSON response
      let message = 'Email sent successfully';
      if (response.data && typeof response.data === 'object') {
        message = response.data.message || 'Email sent successfully';
      }
      toast.success(message);
      handleCloseEmailPopup(); // Close the popup after successful send
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        toast.info('Email sending was cancelled');
      } else {
        console.error('Email error:', error);
        // Handle specific HTTP status codes with French messages
        if (error.response && error.response.status === 403) {
          toast.error("Accès refusé. Vous n'avez pas l'autorisation d'effectuer cette action.");
        } else if (error.response && error.response.status === 404) {
          toast.error("Ressource non trouvée. L'élément demandé n'existe pas.");
        } else {
          toast.error('Error sending email: ' + (error.message || 'Unknown error'));
        }
      }
    } finally {
      setIsEmailSending(false);
      emailAbortControllerRef.current = null;
    }
  };

  const handleOpenExportPopup = () => {
    setIsExportPopupOpen(true);
  };

  const handleCloseExportPopup = () => {
    // Abort any ongoing export request
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
      exportAbortControllerRef.current = null;
      setIsExportLoading(false);
    }
    setIsExportPopupOpen(false);
  };

  const handleExport = async (selectedOptions) => {
    setSelectedExportOptions(selectedOptions);

    if (!selectedOptions) {
      toast.error('No export options selected');
      return;
    }

    // Check what's selected
    const { allData, schoolTypes, otherGroupTypes } = selectedOptions;

    if (!allData && schoolTypes.length === 0 && otherGroupTypes.length === 0) {
      toast.warning('No export options selected');
      return;
    }

    try {
      setIsExportLoading(true);

      // Cancel any ongoing export request
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }

      // Create a new AbortController
      const controller = new AbortController();
      exportAbortControllerRef.current = controller;

      // Make the export request
      await exportData({ ...selectedOptions }, controller.signal);

      toast.success(t('exportData.successMessage'));
      handleCloseExportPopup(); // Close the popup after successful export
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        toast.info('Export was cancelled');
      } else {
        console.error('Export error:', error);
        toast.error('Error during export: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsExportLoading(false);
      exportAbortControllerRef.current = null;
    }
  };

  // Update the export button in the popup
  const renderExportPopupFooter = () => {
    return (
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => {
            // Check if there are any selected options
            const hasSelections =
              selectedExportOptions.allData ||
              selectedExportOptions.schoolTypes.length > 0 ||
              selectedExportOptions.otherGroupTypes.length > 0;

            if (hasSelections) {
              handleExport(selectedExportOptions);
            } else {
              toast.warning('Please select some export options');
            }
          }}
          disabled={isExportLoading}
          className={`${
            isExportLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-10 hover:scale-105 active:scale-95'
          } text-white py-2 px-6 rounded-xl flex items-center gap-2 font-bold transition-all duration-300`}>
          {isExportLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <span>{t('exportData.submitButton')}</span>
          )}
        </button>
      </div>
    );
  };

  // Check if admin has any group permissions
  const hasGroupPermissions = adminData?.assignedRegistrationTypes?.length > 0;

  return (
    <div>
      <div className="min-h-screen flex flex-col bg-white relative">
        <GradientStrip />

        <Header
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
          isLoading={isLoading}
          handleRefreshClick={handleRefreshClick}
          handleOpenEmailPopup={hasGroupPermissions ? handleOpenEmailPopup : undefined}
          handleLogout={handleLogout}
        />

        {/* Dashboard Content */}
        <div className="container mx-auto px-4 pt-6 md:pt-8 pb-8">
          {/* Tab Navigation */}
          {isLoading ? (
            <SkeletonTabBar />
          ) : (
            <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap mb-8 md:mb-16">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`min-h-11 py-2 px-3 md:px-6 rounded-lg flex justify-center items-center space-x-2 text-sm md:text-base ${
                    activeTab === tab.id ? 'bg-teal-30' : 'bg-gray-60 hover:bg-gray-80'
                  }`}
                  aria-pressed={activeTab === tab.id}
                  onClick={() => handleTabClick(tab.id, tab.type)}>
                  {activeTab === tab.id && (
                    <FiEye className="h-4 w-4 shrink-0 text-blue-10" aria-hidden="true" />
                  )}
                  <span className={activeTab === tab.id ? 'text-blue-10' : 'text-gray-50'}>
                    {tab.label}
                  </span>
                </button>
              ))}
              <div className="hidden md:block md:flex-grow"></div>
              <button
                className={`bg-gray-60 text-gray-50 hover:bg-gray-80 hover:scale-105 active:scale-95 transition-all duration-300 min-h-11 py-2 px-4 rounded-lg flex items-center gap-2 col-span-2 justify-self-end text-sm md:text-base ${!hasGroupPermissions ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Export Data"
                onClick={handleOpenExportPopup}
                disabled={!hasGroupPermissions}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="font-medium">Exporter</span>
              </button>
            </div>
          )}

          {/* Title */}
          <h2 className="text-lg md:text-xl font-semibold text-gray-50 mb-8 md:mb-16">
            ENREGISTREMENT ÉTABLISSEMENTS
          </h2>

          {/* Stats Grids */}
          <div className="flex flex-wrap gap-6 sm:gap-4">
            {isLoading ? (
              getSkeletonColumns()
            ) : statsData ? (
              columns.map((column) => (
                <div key={column.id} className="w-full min-w-0 sm:w-[calc(50%-8px)] lg:w-[300px]">
                  <div className="font-semibold text-gray-50 mb-4">{column.title}</div>
                  <div className="flex flex-col space-y-6 border rounded p-2">
                    {column.items.map((item, index) => (
                      <StatCard key={`${column.id}-${index}`} item={item} columnId={column.id} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="w-full text-center py-10">Aucune donnée disponible</div>
            )}
          </div>

          {/* Date Registration Stats */}
          <DateRegistrationStats dateStatsData={dateStatsData} isLoading={isDateStatsLoading} />

          {/* Time Slot Stats */}
          <TimeSlotStats
            timeSlotData={timeSlotData}
            slotReservationData={slotReservationData}
            isLoading={isTimeSlotDataLoading}
          />
        </div>
      </div>

      {/* Email Selection Popup */}
      <AdminPopup
        open={isEmailPopupOpen && !isEmailComposerOpen}
        closePopup={handleCloseEmailPopup}
        variant="teal"
        title={t('groupEmail.title')}
        subTitle={t('groupEmail.subTitle')}
        footer={
          <div className="flex justify-end">
            <button
              onClick={handleWriteEmail}
              disabled={!hasSelections() || isEmailSending}
              className={`py-2 px-6 rounded-xl flex items-center gap-2 font-bold hover:scale-105 active:scale-95 transition-all duration-300 ${
                hasSelections() && !isEmailSending
                  ? 'bg-blue-10 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}>
              <span>{t('groupEmail.selectionSubmitButton')}</span>
            </button>
          </div>
        }>
        <GroupEmail
          onSubmit={handleEmailSelections}
          initialOptions={emailSelections}
          adminData={adminData}
        />
      </AdminPopup>

      {/* Email Composer Popup */}
      <AdminPopup
        open={isEmailPopupOpen && isEmailComposerOpen}
        closePopup={isEmailSending ? null : handleCloseEmailPopup}
        variant="teal"
        title={t('groupEmail.composer.title')}
        subTitle={t('groupEmail.composer.subTitle')}
        customHeightClasses="h-[727px]"
        backButton={{
          show: !isEmailSending,
          onClick: () => setIsEmailComposerOpen(false),
        }}>
        <EmailComposer key={emailComposerKey} onSend={handleSendEmail} isSending={isEmailSending} />
      </AdminPopup>

      {/* Export Data Popup */}
      <AdminPopup
        open={isExportPopupOpen}
        closePopup={handleCloseExportPopup}
        variant="teal"
        title={t('exportData.title')}
        subTitle={t('exportData.subTitle')}
        customWidthClasses="max-w-xl"
        customHeightClasses="max-h-[90vh]"
        footer={renderExportPopupFooter()}>
        <ExportData
          onExport={setSelectedExportOptions}
          adminData={adminData}
          showAllDataOption={true}
        />
      </AdminPopup>
    </div>
  );
};

export default AdminDashboard;
